<template>
  <div id="claim-staffs">
    <div class="content-between">
      <common-text-medium class="mb-4" text="船主担当者情報" />
      <div
        v-for="(staff, index) in vesselOwnerStaffs"
        :key="index + 'owner'"
        @click="onClickEditVesselOwnerStaff(staff.id)"
        class="edit-clickable-area relative mb-8"
      >
        <div
          class="delete-icon-button"
          v-if="canDeleteVesselOwnerStaff"
          @click.stop="openDeleteModal(staff.id)"
        >
          <feather-icon icon="Trash2Icon" svgClasses="w-6 h-6" />
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              所属会社名<span class="required ml-2">*</span>
            </div>
            <common-updated-label
              :updatedAt="getUpdateDateLabel(staff.updated_at)"
              :createdAt="getUpdateDateLabel(staff.created_at)"
            />
          </div>
          <div>{{ staff.company_name }}</div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              担当窓口<span class="required ml-2">*</span>
            </div>
          </div>
          <div>{{ getContactInfoLabel(staff.department) }}</div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              船主担当者名<span class="required ml-2">*</span>
            </div>
          </div>
          <div>
            <span class="mr-6">{{ staff.contact_info.name }}</span>
            <span>{{ staff.contact_info.name_kana }}</span>
          </div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              電話番号（船主担当者）<span class="required ml-2">*</span>
            </div>
          </div>
          <div>{{ staff.contact_info.tel }}</div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              メールアドレス（船主担当者）<span class="required ml-2">*</span>
            </div>
          </div>
          <div>{{ staff.contact_info.email }}</div>
        </div>
      </div>

      <common-add-button
        class="add-button"
        :click="onClickAddVesselOwnerStaff"
        title="担当者情報追加"
        icon="PlusIcon"
      ></common-add-button>

      <vs-divider class="my-6"></vs-divider>

      <common-text-medium class="mb-4" text="保険会社担当者情報" />

      <div
        v-for="(staff, index) in insuranceCompanyStaffs"
        :key="index + 'insurance'"
        @click="onClickEditInsuranceCompanyStaff(staff.id)"
        :class="{ 'edit-clickable-area': isInsuranceCompany }"
        class="relative mb-8"
      >
        <div
          v-if="isInsuranceCompany"
          class="delete-icon-button"
          @click.stop="openDeleteModal(staff.id)"
        >
          <feather-icon icon="Trash2Icon" svgClasses="w-6 h-6" />
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              所属会社名<span class="required ml-2">*</span>
            </div>
            <common-updated-label
              :updatedAt="getUpdateDateLabel(staff.updated_at)"
              :createdAt="getUpdateDateLabel(staff.created_at)"
            />
          </div>
          <div>{{ staff.company_name }}</div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              所属部署<span class="required ml-2">*</span>
            </div>
          </div>
          <div>{{ getDepartmentLabel(staff.department) }}</div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              保険会社担当者名<span class="required ml-2">*</span>
            </div>
          </div>
          <div>
            <span class="mr-6">{{ staff.contact_info.name }}</span>
            <span>{{ staff.contact_info.name_kana }}</span>
          </div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              電話番号（保険会社担当者）<span class="required ml-2">*</span>
            </div>
          </div>
          <div>{{ staff.contact_info.tel }}</div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">
              メールアドレス（保険会社担当者）<span class="required ml-2"
                >*</span
              >
            </div>
          </div>
          <div>{{ staff.contact_info.email }}</div>
        </div>
      </div>

      <common-add-button
        v-if="isInsuranceCompany"
        class="add-button"
        :click="onClickAddInsuranceCompanyStaff"
        title="担当者情報追加"
        icon="PlusIcon"
      ></common-add-button>
    </div>

    <common-modal title="担当者情報登録" ref="EditModal">
      <template v-slot:contents>
        <div class="form-container">
          <common-text-box-step
            class="step-form-item py-4"
            formLabel="所属会社名"
            :required="true"
            :deleteButton="false"
            :value.sync="formCompanyName"
          ></common-text-box-step>

          <div
            class="department-form-item w-full py-4"
            v-if="isFormInsuranceCompany"
          >
            <div class="form-item-title pb-1">
              所属部署名
              <span class="required-mark">*</span>
            </div>
            <hull-dropdown-select
              class="filter-input"
              :selectOptions="insuranceCompanyDepartmentOptions"
              :onSelect="onSelectDepartment"
              :selectedValue="formDepartment"
              placeholder="所属部署名"
              bordered
            ></hull-dropdown-select>
          </div>
          <div
            class="department-form-item w-full py-4"
            v-else-if="isFormVesselOwner"
          >
            <div class="form-item-title pb-1">
              担当窓口
              <span class="required-mark">*</span>
            </div>
            <hull-dropdown-select
              class="filter-input"
              :selectOptions="vesselOwnerDepartmentOptions"
              :onSelect="onSelectDepartment"
              :selectedValue="formDepartment"
              placeholder="担当窓口"
              bordered
            ></hull-dropdown-select>
          </div>

          <div class="name-form-item w-full py-4">
            <div class="form-item-title pb-1">
              <span v-if="isFormInsuranceCompany">保険会社担当者名</span>
              <span v-else-if="isFormVesselOwner">船主担当者名</span>
              <span class="required-mark">*</span>
            </div>
            <div class="flex justify-between">
              <vs-input
                v-model="formName"
                color="#2D9CDB"
                class="full-width-input"
                placeholder="名前"
              />
              <vs-input
                v-model="formNameKana"
                color="#2D9CDB"
                class="full-width-input"
                placeholder="カナ"
              />
            </div>
          </div>

          <common-text-box-step
            v-if="isFormInsuranceCompany"
            class="step-form-item py-4"
            formLabel="電話番号（保険会社担当者）"
            :required="true"
            :deleteButton="false"
            :value.sync="formTel"
            :isValid="isEditInputTelValid"
            invalidMessage="電話番号の形式で入力してください"
          ></common-text-box-step>
          <common-text-box-step
            v-else-if="isFormVesselOwner"
            class="step-form-item py-4"
            formLabel="電話番号（船主担当者）"
            :required="true"
            :deleteButton="false"
            :value.sync="formTel"
            :isValid="isEditInputTelValid"
            invalidMessage="電話番号の形式で入力してください"
          ></common-text-box-step>
          <common-text-box-step
            v-if="isFormInsuranceCompany"
            class="step-form-item py-4"
            formLabel="メールアドレス（保険会社担当者）"
            :required="true"
            :deleteButton="false"
            :value.sync="formEmail"
            :isValid="isEditInputEmailValid"
            invalidMessage="メールアドレスの形式で入力してください"
          ></common-text-box-step>
          <common-text-box-step
            v-else-if="isFormVesselOwner"
            class="step-form-item py-4"
            formLabel="メールアドレス（船主担当者）"
            :required="true"
            :deleteButton="false"
            :value.sync="formEmail"
            :isValid="isEditInputEmailValid"
            invalidMessage="メールアドレスの形式で入力してください"
          ></common-text-box-step>
          <div class="continually-add-check" v-if="isAddMode">
            <vs-checkbox v-model="isContinuallyAdd" color="#48a0f4"
              >続けて作成</vs-checkbox
            >
          </div>

          <div class="button-area">
            <common-button
              class="button"
              title="キャンセル"
              :click="closeEditModal"
              color="#BDBDBD"
              text-color="#757575"
              type="border"
            ></common-button>
            <common-button
              class="button"
              title="登録"
              :click="isAddMode ? callAdd : callEdit"
              :disabled="!isFormValidated"
            ></common-button>
          </div>
        </div>
      </template>
    </common-modal>
    <common-modal title="" ref="DeleteModal">
      <template v-slot:contents>
        <div class="delete-container">
          担当者情報を削除しますか？
          <div class="button-area">
            <common-button
              class="button"
              title="キャンセル"
              :click="closeDeleteModal"
              color="#BDBDBD"
              text-color="#757575"
              type="border"
            ></common-button>
            <common-button
              class="button"
              title="削除"
              :click="() => callDelete(selectedStaffId)"
            ></common-button>
          </div>
        </div>
      </template>
    </common-modal>
  </div>
</template>

<script>
import { ColorMixin, HullInsranceMixin } from "@/mixins";

import CommonTextMedium from "../../components/atoms/CommonTextMedium.vue";
import CommonButton from "../../components/molecules/CommonButton.vue";
import CommonModal from "../../components/organisms/CommonModal.vue";
import CommonTextBoxStep from "../../components/molecules/CommonTextBoxStep.vue";
import CommonAddButton from "../../components/molecules/CommonAddButton.vue";
import CommonUpdatedLabel from "../../components/molecules/CommonUpdatedLabel.vue";
import HullDropdownSelect from "../../components/molecules/HullDropdownSelect.vue";
import ValidationUtil from "@/utils/entity/ValidationUtil";

import { AiseaApiFactory } from "@/api/aisea/apiFactory";
const claimReportStaffRepository = AiseaApiFactory.get(
  "hullInsuranceEnterpriseClaimReportStaff"
);

import {
  HULL_INSURANCE_CONTACT_INFO,
  HULL_INSURANCE_DEPARTMENT,
} from "@/config/const";
import moment from "moment";

export default {
  name: "O.ClaimSummary",
  mixins: [ColorMixin, HullInsranceMixin],
  components: {
    CommonTextMedium,
    CommonButton,
    CommonModal,
    CommonTextBoxStep,
    CommonAddButton,
    HullDropdownSelect,
    CommonUpdatedLabel,
  },
  data() {
    return {
      enterpriseKind: null,

      claimReportId: null,
      staffsData: null,

      initialFormInsuranceCompanyName: "東京海上日動火災保険",

      formCompanyName: "",
      formDepartment: null,
      formName: "",
      formNameKana: "",
      formTel: "",
      formEmail: "",
      formEnterpriseKind: null,
      formVersion: null,
      isContinuallyAdd: false,

      selectedStaffId: null,
    };
  },
  computed: {
    canDeleteVesselOwnerStaff() {
      return this.vesselOwnerStaffs.length > 1;
    },
    isVesselOwner() {
      return this.enterpriseKind == 1;
    },
    isInsuranceCompany() {
      return this.enterpriseKind == 2;
    },
    isFormVesselOwner() {
      return this.formEnterpriseKind == 1;
    },
    isFormInsuranceCompany() {
      return this.formEnterpriseKind == 2;
    },
    vesselOwnerDepartmentOptions() {
      return HULL_INSURANCE_CONTACT_INFO.map(element => {
        return {
          text: element.label,
          value: element.id,
        };
      });
    },
    insuranceCompanyDepartmentOptions() {
      return HULL_INSURANCE_DEPARTMENT.map(element => {
        return {
          text: element.label,
          value: element.id,
        };
      });
    },
    isEditInputTelValid() {
      return this.formTel == "" || ValidationUtil.validateTel_jp(this.formTel);
    },
    isEditInputEmailValid() {
      return (
        this.formEmail == "" || ValidationUtil.isValidEmail(this.formEmail)
      );
    },

    isFormValidated() {
      return (
        this.formCompanyName.trim().length > 0 &&
        this.formDepartment != null &&
        this.formName.trim().length > 0 &&
        this.formNameKana.trim().length > 0 &&
        this.formTel.trim().length > 0 &&
        this.formEmail.trim().length > 0 &&
        this.isEditInputTelValid &&
        this.isEditInputEmailValid
      );
    },
    vesselOwnerStaffs() {
      return this.staffsData != null
        ? this.staffsData.filter(element => element.enterprise_kind == 1)
        : null;
    },
    insuranceCompanyStaffs() {
      return this.staffsData != null
        ? this.staffsData.filter(element => element.enterprise_kind == 2)
        : null;
    },
    isAddMode() {
      return this.selectedStaffId == null;
    },
  },
  methods: {
    onClickAddVesselOwnerStaff() {
      this.selectedStaffId = null;
      this.clearStaffEditForm();
      this.formEnterpriseKind = 1;
      this.openEditModal();
    },
    onClickAddInsuranceCompanyStaff() {
      this.selectedStaffId = null;
      this.clearStaffEditForm();
      this.formCompanyName = this.initialFormInsuranceCompanyName;
      this.formEnterpriseKind = 2;
      this.openEditModal();
    },
    onClickEditVesselOwnerStaff(staffId) {
      this.selectedStaffId = staffId;
      this.formEnterpriseKind = 1;
      const found = this.staffsData.find(element => element.id == staffId);
      this.setEditModalForm(
        found.company_name,
        found.department,
        found.contact_info.name,
        found.contact_info.name_kana,
        found.contact_info.tel,
        found.contact_info.email,
        found.enterprise_kind,
        found.version
      );
      this.openEditModal();
    },
    onClickEditInsuranceCompanyStaff(staffId) {
      this.selectedStaffId = staffId;
      this.formEnterpriseKind = 2;
      const found = this.staffsData.find(element => element.id == staffId);
      this.setEditModalForm(
        found.company_name,
        found.department,
        found.contact_info.name,
        found.contact_info.name_kana,
        found.contact_info.tel,
        found.contact_info.email,
        found.enterprise_kind,
        found.version
      );
      this.openEditModal();
    },
    getUpdateDateLabel(value) {
      return moment(value).format("YYYY/MM/DD HH:mm");
    },
    onSelectDepartment(value) {
      this.formDepartment = value;
    },
    async callFetch() {
      const res = await claimReportStaffRepository.fetch(this.claimReportId);
      if (res.status == true) {
        this.staffsData = res.data.report_staffs;
      } else {
        this.$vs.notify({
          title: "関係者情報の取得に失敗しました。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
    },
    async callAdd() {
      const res = await claimReportStaffRepository.add(this.claimReportId, {
        company_name: this.formCompanyName,
        contact_info: {
          name: this.formName,
          tel: this.formTel,
          email: this.formEmail,
          name_kana: this.formNameKana,
        },
        department: this.formDepartment,
        enterprise_kind: this.formEnterpriseKind,
      });

      if (res.status == true) {
        this.$vs.notify({
          title: "関係者情報を追加しました",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
        this.clearStaffEditForm();
        if (this.isContinuallyAdd && this.isInsuranceCompany) {
          this.formCompanyName = this.initialFormInsuranceCompanyName;
        } else if (!this.isContinuallyAdd) {
          this.closeEditModal();
        }
        this.callFetch();
      } else if (res.code == 400) {
        this.$vs.notify({
          title: res.message,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      } else {
        this.$vs.notify({
          title: "関係者情報の追加に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
    },

    async callEdit() {
      const res = await claimReportStaffRepository.update(
        this.claimReportId,
        this.selectedStaffId,
        {
          company_name: this.formCompanyName,
          contact_info: {
            name: this.formName,
            tel: this.formTel,
            email: this.formEmail,
            name_kana: this.formNameKana,
          },
          department: this.formDepartment,
          enterprise_kind: this.formEnterpriseKind,
          version: this.formVersion,
        }
      );
      if (res.status == true) {
        this.$vs.notify({
          title: "関係者情報を更新しました",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
        this.clearStaffEditForm();
        this.closeEditModal();
        this.callFetch();
      } else if (res.code == 400) {
        this.$vs.notify({
          title: res.message,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      } else {
        this.$vs.notify({
          title: "関係者情報の更新に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
    },
    async callDelete(staffId) {
      this.$vs.loading();
      const res = await claimReportStaffRepository.delete(
        this.claimReportId,
        staffId
      );
      this.$vs.loading.close();
      if (res.status == true) {
        this.$vs.notify({
          title: "担当者情報を削除しました",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
      } else {
        this.$vs.notify({
          title: "担当者情報の削除に失敗しました。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
      this.closeDeleteModal();
      this.callFetch();
    },
    getContactInfoLabel(id) {
      return HULL_INSURANCE_CONTACT_INFO.find(item => item.id === id).label;
    },
    getDepartmentLabel(id) {
      return HULL_INSURANCE_DEPARTMENT.find(item => item.id === id).label;
    },
    openEditModal() {
      this.$refs.EditModal.show();
    },
    setEditModalForm(
      companyName,
      department,
      name,
      nameKana,
      tel,
      email,
      enterpriseKind,
      version
    ) {
      this.clearStaffEditForm();

      this.formCompanyName = companyName;
      this.formDepartment = department;
      this.formName = name;
      this.formNameKana = nameKana;
      this.formTel = tel;
      this.formEmail = email;
      this.formEnterpriseKind = enterpriseKind;
      this.formVersion = version;
    },
    closeEditModal() {
      this.clearStaffEditForm();
      this.$refs.EditModal.close();
    },
    openDeleteModal(id) {
      this.selectedStaffId = id;
      this.$refs.DeleteModal.show();
    },
    closeDeleteModal() {
      this.$refs.DeleteModal.close();
    },
    clearStaffEditForm() {
      this.formCompanyName = "";
      this.formDepartment = null;
      this.formName = "";
      this.formNameKana = "";
      this.formTel = "";
      this.formEmail = "";
      this.formVersion = null;
    },
  },
  async created() {
    this.claimReportId = this.$route.params.id;
    this.enterpriseKind = await this.getEnterpriseKind();
    await this.callFetch();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "@/assets/scss/aisea/hull-insurance.scss";

#claim-staffs {
  font-family: $aisea-font-family;
  .title {
    font-weight: bold;
  }
  .required {
    color: $aisea-red;
  }

  .updated-date-label {
    font-size: 12px;
  }

  .add-button {
    width: fit-content;
    display: flex;
    margin-top: 30px;
    margin-bottom: 30px;
    margin-left: auto;
    margin-right: auto;
  }
  .edit-clickable-area {
    cursor: pointer;
    transition: background-color 0.3s;
    &:hover {
      background: $hover-background-blue;
      .delete-icon-button {
        display: initial;
      }
    }
    .delete-icon-button {
      display: none;
      position: absolute;
      top: 10px;
      right: 10px;
      padding: 4px;
      border-radius: 4px;
      background: $hover-background-blue;
    }
  }
}

.form-container {
  width: 414px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: auto;
  margin-right: auto;

  .continually-add-check {
    margin-top: 10px;
    margin-bottom: 10px;
    width: 100%;
  }
  .button-area {
    display: flex;
    justify-content: space-between;
    width: 320px;
    margin-top: 20px;
    margin-bottom: 10px;
    .button {
      width: 140px;
    }
  }

  .required-mark {
    color: red;
  }
}
.delete-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  .button-area {
    display: flex;
    justify-content: space-between;
    width: 320px;
    margin-top: 20px;
    margin-bottom: 10px;
    .button {
      width: 140px;
    }
  }
}
</style>
